import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flag, LocateFixed } from 'lucide-react';
import { COURSE_CANVAS, MOUNTAIN_CANVAS, TRAINING_STAGES, getCoursePoint, getTrainingCourse } from '../config/trainingCourse';
import { t } from '../i18n';
import { formatCompactInteger } from '../utils/numberFormat';
import './TrainingCourse.css';

const label = (key, replacements) => t(`accountActivity.course.${key}`, undefined, replacements);
const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Interpolate path distance, not a straight line between old/new screen positions.
function useCourseMotion(course) {
  const [motion, setMotion] = useState({ key: `${course.cycle}-${course.stage.id}`, ratio: course.ratio });
  const previous = useRef(motion);
  const key = `${course.cycle}-${course.stage.id}`;
  useEffect(() => {
    const from = previous.current;
    const next = { key, ratio: course.ratio };
    if (from.key !== key || reducedMotion() || from.ratio === next.ratio) {
      previous.current = next;
      setMotion(next);
      return undefined;
    }
    let frame;
    let start;
    const tick = (time) => {
      if (start === undefined) start = time;
      const progress = Math.min(1, (time - start) / 650);
      const current = { key, ratio: from.ratio + (next.ratio - from.ratio) * progress };
      previous.current = current;
      setMotion(current);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [key, course.ratio]);
  return getCoursePoint(course.stage.path, motion.key === key ? motion.ratio : course.ratio);
}

export default function TrainingCourse({ level, pace, goalEstimate }) {
  const course = getTrainingCourse(level);
  const point = useCourseMotion(course);
  const character = useRef(null);
  const stageTargets = useRef({});
  const previousLevel = useRef(course.level);
  const [newFlags, setNewFlags] = useState([]);

  const centerOn = useCallback((element) => {
    element?.scrollIntoView?.({
      block: 'center', inline: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth',
    });
  }, []);

  // The course is part of the page now. Only explicit navigation should move
  // the page; mounting, refreshing progress and level animations must not jump it.
  useEffect(() => {
    const previous = getTrainingCourse(previousLevel.current);
    const current = getTrainingCourse(level);
    previousLevel.current = current.level;
    if (previous.level === current.level) return undefined;
    setNewFlags(current.completedStages.filter((id) => previous.cycle !== current.cycle || !previous.completedStages.includes(id)));
    const timer = setTimeout(() => setNewFlags([]), 1000);
    return () => clearTimeout(timer);
  }, [level]);

  const renderStage = (stage, index, canvas) => {
    const completed = course.completedStages.includes(stage.id);
    const endpoint = stage.path[stage.path.length - 1];
    const endLevel = course.offset + stage.endLevel;
    const isMountain = stage.id === 'mountain';
    const stageBottom = TRAINING_STAGES[index + 1]?.top || canvas.height;
    const captionRatio = stage.id === 'meadow' ? 0.62 : stage.id === 'river' ? 0.75 : 0.78;
    const captionTop = stage.top + (stageBottom - stage.top) * captionRatio;
    return <React.Fragment key={stage.id}>
      <div className="training-course-stage-label" data-stage={stage.id} ref={(node) => { stageTargets.current[stage.id] = node; }}
        style={{ top: `${(stage.top / canvas.height) * 100}%` }}>
        <span>{String(index + 1).padStart(2, '0')}</span>
        <strong>{label(stage.id)}</strong>
      </div>
      {course.stage.id === stage.id && <p className={`training-course-stage-caption${isMountain ? ' is-mountain' : ''}`} data-stage={stage.id}
        style={isMountain ? undefined : { top: `${captionTop / canvas.height * 100}%` }}>
        {label(`captions.${stage.id}`)}
      </p>}
      <div className={`training-course-checkpoint${completed ? ' is-complete' : ''}${index === 4 ? ' is-summit' : ''}${newFlags.includes(stage.id) ? ' is-new' : ''}`}
        data-stage={stage.id} data-complete={completed}
        aria-label={label(completed ? 'checkpointComplete' : 'checkpoint', { level: endLevel })}
        style={{ left: `${((endpoint.x + 38) / canvas.width) * 100}%`, top: `${(endpoint.y / canvas.height) * 100}%` }}>
        {completed && <Flag aria-hidden="true" />}
        <span>Lv. {endLevel}</span>
        {index === 4 && goalEstimate && <div className="training-course-summit-estimate">{goalEstimate}</div>}
      </div>
    </React.Fragment>;
  };
  const renderCharacter = (canvas) => <div ref={character} className="training-course-character" data-stage={course.stage.id}
    aria-label={label('character', { level: course.level, stage: label(course.stage.id) })}
    style={{ left: `${point.x / canvas.width * 100}%`, top: `${point.y / canvas.height * 100}%`,
      height: `${course.stage.height * point.scale / canvas.height * 100}%`, transform: `translate(-50%, -${course.stage.anchor * 100}%)` }}>
    <img src={course.stage.image} alt="" draggable="false" />
    <span className="training-course-you">{label('you')}</span>
    {Number.isFinite(pace) && <span className="training-course-pace"
      aria-label={`${t('accountActivity.journey.pace')}: ${t('accountActivity.journey.perDay', undefined, { count: formatCompactInteger(pace) })}`}>
      <span>PACE</span><strong>{formatCompactInteger(pace)}</strong>
    </span>}
  </div>;

  return <section className="training-course" aria-label={label('title')}>
    <div className="training-course-summary">
      <div><strong>{label('round', { count: course.cycle })}</strong><span>{label('completedCourses', { count: course.completedCourses })}</span></div>
      <button type="button" onClick={() => centerOn(character.current)}><LocateFixed size={16} aria-hidden="true" />{label('locate')}</button>
    </div>
    <div className="training-course-progress" role="progressbar" aria-label={label('progress')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(course.percent)}>
      <span style={{ width: `${course.percent}%` }} />
    </div>
    <nav className="training-course-stages" aria-label={label('stages')}>
      {TRAINING_STAGES.map((stage, index) => <button type="button" key={stage.id}
        aria-current={course.stage.id === stage.id ? 'step' : undefined}
        onClick={() => centerOn(stageTargets.current[stage.id])}>
        <span>{course.completedStages.includes(stage.id) ? <Flag size={13} aria-hidden="true" /> : index + 1}</span>{label(stage.id)}
      </button>)}
    </nav>
    <p className="training-course-help">{label('browse')}</p>
    <div className="training-course-map" role="region" aria-label={label('map')}>
      <div className="training-course-canvas" style={{ aspectRatio: `${COURSE_CANVAS.width} / ${COURSE_CANVAS.height}` }}>
        <img className="training-course-background" src={COURSE_CANVAS.image} width={724} height={1510} alt="" draggable="false" />
        {TRAINING_STAGES.slice(0, 4).map((stage, index) => renderStage(stage, index, COURSE_CANVAS))}
        {course.stageIndex < 4 && renderCharacter(COURSE_CANVAS)}
      </div>
      <div className="training-course-gap" aria-hidden="true" />
      <div className="training-course-canvas" style={{ aspectRatio: `${MOUNTAIN_CANVAS.width} / ${MOUNTAIN_CANVAS.height}` }}>
        <img className="training-course-background" src={MOUNTAIN_CANVAS.image} width={900} height={1200} alt="" draggable="false" loading="lazy" />
        {renderStage(TRAINING_STAGES[4], 4, MOUNTAIN_CANVAS)}
        {course.stageIndex === 4 && renderCharacter(MOUNTAIN_CANVAS)}
      </div>
    </div>
  </section>;
}
